import React from 'react';
import {
  type CustomBlockTypeEnum,
  isHistoricalSubject,
  PRO_FORMA_HISTORICAL_AS_OF_DATE_PARAMETERIZED_BLOCKS,
  PRO_FORMA_HISTORICAL_SIMULATION_DATE_BLOCKS,
  withSuspense,
} from 'venn-utils';
import { useRecoilValue } from 'recoil';
import { blockLimitedRequestSubjects, blockSettings, isProFormaBlockOrMetric } from 'venn-state';
import { Info } from 'venn-ui-kit';

const getHistoricalProFormaSubjectsMessage = (blockType: CustomBlockTypeEnum) => {
  // Blocks with explicit as of date selector don't need a notice since it's clear what allocations are being used
  if (PRO_FORMA_HISTORICAL_AS_OF_DATE_PARAMETERIZED_BLOCKS.includes(blockType)) {
    return null;
  }

  if (PRO_FORMA_HISTORICAL_SIMULATION_DATE_BLOCKS.includes(blockType)) {
    return 'Portfolios with historical allocations on this block use latest uploaded allocations, adjusted for market drift between allocation date and simulation start date.';
  }

  if (blockType === 'CORRELATION') {
    return 'Portfolios with historical allocations on this block use latest uploaded allocations.';
  }

  if (blockType === 'SCENARIO') {
    return 'Portfolios with historical allocations on this block use latest uploaded allocations, adjusted for market drift between allocation date and forecast start date.';
  }

  return null;
};

/** Historical Pro-Forma Portfolio Warning. */
export const HistoricalProFormaSubjectsMessage = withSuspense(
  null,
  ({ selectedBlockId }: { selectedBlockId: string }) => {
    const blockType = useRecoilValue(blockSettings(selectedBlockId)).customBlockType;
    const hasHistoricalSubjects = useRecoilValue(blockLimitedRequestSubjects(selectedBlockId)).some(
      isHistoricalSubject,
    );
    const isProForma = useRecoilValue(isProFormaBlockOrMetric(selectedBlockId));

    const shouldShowMessage = hasHistoricalSubjects && isProForma;

    if (!shouldShowMessage) {
      return null;
    }

    const message = getHistoricalProFormaSubjectsMessage(blockType);

    return message && <Info text={message} />;
  },
);
